/**
 * The catalogue of features this app *has* — not what any brand does with them.
 *
 * A brand profile (`brands/<key>.json`, `contract/schema/brand.schema.json`)
 * says on/off, label and slot. This says what there is to say that about, and
 * **where each feature is allowed to go**. Keeping the two apart is the whole
 * design: the profile is data anybody may edit, and without a list of legal
 * placements it would be a way to build an app that does not render.
 *
 * ## The allowed list is also the default
 *
 * `desktop: ["header", "appMenu"]` means two things at once — the feature may
 * sit in either, and absent an override it sits in `header`. **First is the
 * default**, so there is no second field to disagree with the first, and a
 * profile that names no slot is a profile that gets today's app.
 *
 * A shell left out entirely (`mobile` absent) means the feature does not render
 * on that shell at all and no profile can put it there. That is not a slight on
 * the feature: `docs/features/mobile.md` is a separate arrangement rather than
 * a reflow, and several things that earn their place on a 27" monitor earn
 * nothing on a phone.
 *
 * ## `capability` is the seam with entitlements, and it is not the same axis
 *
 * Where a feature is also gated server-side the capability string is recorded
 * here, because the two answers are different questions and get confused
 * otherwise:
 *
 * | | asks | resolved | failure |
 * |---|---|---|---|
 * | **brand** | does *this app* have it? | at build, from a file | absent, no trace |
 * | **entitlement** | may *this account* use it? | per request, from rows | locked, an upsell |
 *
 * They intersect, and **the brand is applied first**. Off in the brand while a
 * plan grants the capability is the normal case rather than an inconsistency —
 * QuFolio and StockBullBear share one backend and therefore one set of grant
 * rows while showing different features. Recording the pairing here is what
 * lets that be checked instead of rediscovered.
 *
 * Nothing here is enforcement. Hiding a control does not refuse the request
 * behind it; anything that must actually be refused is an entitlement, and
 * `docs/features/entitlements.md` is where that lives.
 *
 * ## Adding a feature
 *
 * Add a row. Every existing profile keeps working and picks it up at its
 * default, because a profile states only its differences. Deleting a row is the
 * direction that needs care: a profile still naming it fails `checkProfile`,
 * which is the intended noise rather than a silent no-op.
 */

/** The two shells. A third is a project, not a data edit. */
export const SHELLS = ["desktop", "mobile"] as const;

export type Shell = (typeof SHELLS)[number];

/**
 * Where the wide shell can render something.
 *
 * `header` is the top row of the app — `LayoutBar` today, which already carries
 * layouts, the theme editor, admin, the plan screen and the trades toggle. It
 * is named for what it is rather than for the component that draws it, because
 * the component is an implementation and the slot is a contract.
 *
 * `hidden` is a real placement and not the absence of one: a feature can be
 * enabled, reachable by keyboard or by a deep link, and have no chrome. It is
 * how a brand keeps something working without advertising it.
 */
export const DESKTOP_SLOTS = [
  "pane",
  "header",
  "appMenu",
  "chartToolbar",
  "groupBar",
  "dataPanel",
  "symbolDialog",
  "contextMenu",
  "indicatorPicker",
  "hidden",
] as const;

/** Where the narrow shell can render something. */
export const MOBILE_SLOTS = ["tab", "more", "chartToolbar", "hidden"] as const;

export type DesktopSlot = (typeof DESKTOP_SLOTS)[number];
export type MobileSlot = (typeof MOBILE_SLOTS)[number];
export type AnySlot = DesktopSlot | MobileSlot;

/**
 * Slots that hold a fixed number of things, and the number.
 *
 * **The tab bar holds five features.** `MobileTabBar` draws six keys and the
 * sixth is More, which opens the `more` surface — chrome, not a feature, and
 * not something a brand may evict. It was three until Community took a tab
 * (owner, 2026-09-29: "a tab in the bottom bar for now"), and four until Data
 * took one (owner, 2026-10-13); six keys is about 62 px each at 375 px wide,
 * which fits "Community" at the bar's 11 px label size. A claimant past the capacity is not a layout that degrades
 * gracefully; it is one that silently loses a tab, which is why this is
 * declared rather than discovered.
 *
 * Unlisted means unbounded: a menu grows, a toolbar wraps.
 */
export const SLOT_CAPACITY: {
  readonly [S in Shell]?: { readonly [slot: string]: number };
} = {
  mobile: { tab: 5 },
};

/** How the studio groups the matrix. Presentation only — nothing branches on it. */
export type FeatureGroup = "screens" | "chart" | "workspace" | "account";

export interface FeatureDef {
  /** camelCase, stable, and what a profile names. Renaming one is a migration. */
  readonly id: string;
  /** What the app calls it with no brand override. */
  readonly label: string;
  readonly group: FeatureGroup;
  /**
   * Cannot be switched off, by anybody, at any layer. A chart platform with no
   * chart is not a brand — it is a bug report with a name.
   */
  readonly core?: true;
  /**
   * Features that must be on for this one to mean anything. A requirement that
   * is off takes this one down with it, transitively — a Plan button that opens
   * an empty screen because the journal behind it was disabled is worse than no
   * button, and it is the kind of thing nobody notices until a customer does.
   */
  readonly requires?: readonly string[];
  /**
   * The server capability this feature is *also* gated by, where there is one.
   * Documentation of a seam, not a check — see the header.
   */
  readonly capability?: string;
  /**
   * Where it sits within its slot by default, ascending.
   *
   * Absent means registry position × 10, which is right for most things and
   * wrong wherever the app already has an arrangement somebody chose. The
   * mobile bar is the case that forced this: it has always read Screener,
   * Chart, Journal, and deriving that from the listing order below would mean
   * listing the screener before the chart — contorting the catalogue to encode
   * a layout, in a file that is supposed to be a catalogue.
   *
   * Sparse, for the profile's reason: a brand inserting something between two
   * of these moves one number, not a row of them.
   */
  readonly order?: number;
  /** Legal desktop slots, first being the default. Absent: no desktop chrome. */
  readonly desktop?: readonly DesktopSlot[];
  /** Legal mobile slots, first being the default. Absent: no mobile chrome. */
  readonly mobile?: readonly MobileSlot[];
}

/**
 * Every feature, and where each may sit.
 *
 * The defaults describe **the app as it is today**, so that resolving a brand
 * with no profile is a no-op. Each one is verified against the surface that
 * draws it as that surface is converted to read this registry — until then a
 * default here is a statement of intent, and the conversion is what makes it
 * load-bearing.
 */
/**
 * The table itself, kept at its literal type so `FeatureId` can be read off it.
 *
 * `as const satisfies` checks every row against `FeatureDef` *and* keeps the
 * narrow tuple — which is what makes the id union real, and also what makes the
 * rows unusable as `FeatureDef`s: a member with no `requires` does not have the
 * property at all, so `def.requires` fails to compile on the union. `FEATURES`
 * below is the same array at the wider type, and is what everything iterates.
 */
const FEATURE_TABLE = [
  // ── Screens ──────────────────────────────────────────────────────────
  {
    id: "charts",
    label: "Chart",
    group: "screens",
    core: true,
    order: 10,
    desktop: ["pane"],
    mobile: ["tab"],
  },
  {
    id: "data",
    label: "Data",
    group: "screens",
    // The symbol's data panel — `SymbolDataTabs`, every view the desktop panel
    // has — for the chart on screen. On a phone it is a tab next to the chart
    // (owner, 2026-10-13); on the desktop it opens from the chart toolbar.
    order: 15,
    desktop: ["chartToolbar", "hidden"],
    mobile: ["tab", "more", "hidden"],
  },
  {
    id: "screener",
    label: "Screener",
    group: "screens",
    // First on the mobile bar, which is where it has always been.
    order: 0,
    // The leftmost tab on a phone, a section in the layout grid on a desktop.
    desktop: ["pane"],
    mobile: ["tab", "more"],
  },
  {
    id: "journal",
    label: "Journal",
    group: "screens",
    capability: "feat:journal",
    order: 20,
    // **"Journal", because the desktop tab already says so.** Converting the
    // mobile bar found this feature wearing three names in one app — "Journal"
    // on the layout strip, "Trade" on the phone's tab, "Trades" in prose — and
    // one label is the whole point of a brand being able to rename it: a
    // registry with a second short form would leave a brand that renamed this
    // to "Positions" still saying "Trade" on the tab bar. So the phone's tab
    // now reads what the desktop tab has always read.
    desktop: ["header", "appMenu"],
    mobile: ["tab", "more"],
  },
  {
    id: "plan",
    label: "Trading Plan",
    group: "screens",
    // The journal is the store a plan is written into; without it this opens
    // onto nothing. See docs/features/trading-plan.md.
    requires: ["journal"],
    // A `GridToolbarButton` on the chart strip (`App.tsx`, "Plans for {symbol}"),
    // not a header button — corrected from the header when this was converted.
    desktop: ["chartToolbar", "contextMenu", "appMenu"],
    // No mobile chrome of its own: on a phone a plan is a group *inside* the
    // journal view (`TradesView`'s `tradeGroup`), so it is reached by the
    // journal's tab and has no placement to give a brand.
  },

  {
    id: "community",
    label: "Community",
    group: "screens",
    // A group's own space, its topics and its Open House — docs/plans/community.md.
    // On the desktop it is a key on the chart strip that opens the panel in the
    // dock (the screener's cell, D56); on a phone it is a tab (owner, 2026-09-29:
    // "a tab in the bottom bar for now, we change it later").
    order: 30,
    desktop: ["chartToolbar", "hidden"],
    mobile: ["tab", "more", "hidden"],
  },

  // ── Chart ────────────────────────────────────────────────────────────
  {
    id: "pip",
    label: "Info Panel",
    group: "chart",
    desktop: ["chartToolbar"],
    mobile: ["chartToolbar", "hidden"],
  },
  {
    id: "annotations",
    label: "Draw",
    group: "chart",
    desktop: ["chartToolbar"],
    mobile: ["chartToolbar", "hidden"],
  },
  {
    id: "seasonality",
    label: "Seasonality",
    group: "chart",
    // Spelled as a category server-side (`ind:`) so gating a second indicator
    // is a row rather than a code change — docs/features/entitlements.md.
    capability: "ind:Seasonality",
    desktop: ["indicatorPicker"],
  },
  {
    id: "ratioCharts",
    label: "Ratio",
    group: "chart",
    desktop: ["symbolDialog"],
    mobile: ["more", "hidden"],
  },
  {
    id: "fundamentals",
    label: "Fundamentals",
    group: "chart",
    desktop: ["dataPanel"],
    mobile: ["more", "hidden"],
  },

  // ── Workspace ────────────────────────────────────────────────────────
  {
    id: "layouts",
    label: "Layouts",
    group: "workspace",
    desktop: ["header", "appMenu"],
    mobile: ["more"],
  },
  {
    id: "tracklists",
    label: "Track Lists",
    group: "workspace",
    desktop: ["groupBar", "appMenu"],
    mobile: ["more"],
  },
  {
    id: "themes",
    label: "Theme",
    group: "workspace",
    // Verified on conversion: this is the ☰ menu's "Theme…" row, reached
    // through `onOpenSettings`. It was declared `header` first, which the menu
    // then disproved — the header carries the layouts and the trades toggle.
    desktop: ["appMenu", "header"],
    mobile: ["more"],
  },

  // ── Account and agent ────────────────────────────────────────────────
  {
    id: "describe",
    label: "Describe",
    group: "account",
    capability: "feat:describe",
    desktop: ["contextMenu", "hidden"],
  },
  {
    id: "analyst",
    label: "Analyst",
    group: "account",
    capability: "feat:analyst",
    desktop: ["dataPanel", "hidden"],
    mobile: ["more", "hidden"],
  },
  {
    id: "agent",
    label: "QueVee",
    group: "account",
    capability: "feat:agent",
    // The chat panel (docs/features/chat.md): on the desktop a key on the chart
    // strip below Community that opens it in the dock, as Community's does; on
    // a phone a row in More that opens it full screen. `hidden` keeps the
    // feature (Ask AI still opens the panel) with no key of its own. `header`
    // and `appMenu` are not drawn by anything yet — kept so a profile that
    // named one still validates, and resolves to no key rather than failing.
    desktop: ["chartToolbar", "hidden", "header", "appMenu"],
    mobile: ["more", "hidden"],
  },
  {
    id: "admin",
    label: "Admin",
    group: "account",
    // Already gated by role; a brand may additionally decide it has no such
    // screen. The two gates are independent and both apply.
    // Verified on conversion: the ☰ menu's "Clubs & access…" row.
    desktop: ["appMenu", "header", "hidden"],
  },
] as const satisfies readonly FeatureDef[];

/** Literal union of every feature id — what a resolved brand is keyed by. */
export type FeatureId = (typeof FEATURE_TABLE)[number]["id"];

/**
 * Every feature, in registry order — the order that breaks a tie on `order`.
 *
 * Widened to `FeatureDef` so an optional field is readable on every row, but the
 * `id` is pinned back to the literal union: iterating this and calling
 * `brand.enabled(def.id)` is the common move, and it should not need a cast.
 */
export const FEATURES: readonly (FeatureDef & { readonly id: FeatureId })[] =
  FEATURE_TABLE;

/** Every id, in the same order. */
export const FEATURE_IDS: readonly FeatureId[] = FEATURE_TABLE.map((f) => f.id);

const BY_ID = new Map<string, FeatureDef>(FEATURES.map((f) => [f.id, f]));

/** The definition for an id, or `undefined` if nothing by that name exists. */
export function featureById(id: string): FeatureDef | undefined {
  return BY_ID.get(id);
}

/** Whether a string names a feature this build has. */
export function isFeatureId(id: string): id is FeatureId {
  return BY_ID.has(id);
}

/**
 * The slots a feature may occupy in one shell — empty if it has no chrome there.
 *
 * The single place that knows a shell's slot list is read off the feature
 * rather than off a parallel table, which is what keeps "may it go here" and
 * "where does it go by default" from ever disagreeing.
 */
export function slotsFor(def: FeatureDef, shell: Shell): readonly AnySlot[] {
  return (shell === "desktop" ? def.desktop : def.mobile) ?? [];
}

/** A feature's default slot in one shell, or `null` if it has no chrome there. */
export function defaultSlot(def: FeatureDef, shell: Shell): AnySlot | null {
  return slotsFor(def, shell)[0] ?? null;
}

/** How many features a slot holds, or `null` for unbounded. */
export function capacityOf(shell: Shell, slot: string): number | null {
  return SLOT_CAPACITY[shell]?.[slot] ?? null;
}
